import React, { useCallback, useState, useRef } from 'react';
import { Box, Typography, Grid, Button } from "@mui/material";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import { DynamicFieldAdvanced } from '../../../../../../components/common/input/DynamicFieldAdvanced';
import { useSelector, useDispatch } from 'react-redux';
import { setBankDetails, setStepCompletion } from '../../../../../../store';
import { saveEmployeeBankDetails } from '../../../../../../api/employeeApi';
import { useNotification } from '../../../../../../hooks/useNotification';

const BankDetails = ({ onNext, onBack, employeeId }) => {
    const dispatch = useDispatch();
    const { showNotification, Notification } = useNotification();
    const bankDetails = useSelector((state) => state.basicDetails.bankDetails) || {};
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [bankFile, setBankFile] = useState(null);
    const fileInputRef = useRef(null);

    const handleFileChoose = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const handleFileInputChange = useCallback((event) => {
        const files = Array.from(event.target.files);
        if (files.length > 0) {
            setBankFile(files[0]);
        }
        event.target.value = '';
    }, []);

    const handleFieldChange = useCallback((name, value) => {
        dispatch(setBankDetails({ [name]: value }));

        // Clear error for this field
        setErrors((prev) => {
            if (prev[name]) {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            }
            return prev;
        });
    }, [dispatch]);

    const validate = () => {
        const newErrors = {};
        if (!bankDetails.accountName?.trim()) {
            newErrors.accountName = 'Account holder name is required';
        }
        if (!bankDetails.accountNumber?.trim()) {
            newErrors.accountNumber = 'Account number is required';
        } else if (bankDetails.accountNumber.trim().length <= 9) {
            newErrors.accountNumber = 'Account number must be more than 9 digits';
        }
        if (!bankDetails.bankName?.trim()) {
            newErrors.bankName = 'Bank name is required';
        }
        if (!bankDetails.ifscCode?.trim()) {
            newErrors.ifscCode = 'IFSC code is required';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        // Mark all fields as touched
        setTouched({
            accountName: true,
            accountNumber: true,
            bankName: true,
            ifscCode: true,
            branchName: true,
        });

        if (validate() && onNext) {
            if (!employeeId) {
                setErrors({ ...errors, submit: "Employee ID is missing" });
                return;
            }

            setIsSubmitting(true);
            try {
                const formData = new FormData();
                formData.append('data', JSON.stringify(bankDetails));
                if (bankFile) {
                    formData.append('passbookDocument', bankFile);
                }

                await saveEmployeeBankDetails(employeeId, formData);
                dispatch(setStepCompletion({ step: 'bankDetails', isCompleted: true }));

                setErrors({});
                setTouched({});

                if (employeeId) {
                    showNotification("Bank details updated successfully", "success");
                }
                if (onNext) {
                    onNext();
                }
            } catch (error) {
                console.error("Error saving bank details:", error);
                showNotification(error.message || "Failed to update bank details", "error");
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    const handleSkip = () => {
        if (onNext) {
            onNext();
        }
    };

    return (
        <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#030007', mb: 3.5, display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Box sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '8px',
                    bgcolor: '#F5EEFC',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}>
                    <AccountBalanceOutlinedIcon sx={{ color: '#7522D1', fontSize: 18 }} />
                </Box>
                Bank Details & Settlement
            </Typography>

            <Grid container spacing={3}>
                <Grid item size={{ xs: 12, md: 6 }}>
                    <DynamicFieldAdvanced
                        type="text"
                        label="Account Holder Name"
                        name="accountName"
                        value={bankDetails.accountName}
                        onChange={handleFieldChange}
                        required
                        error={touched.accountName && !!errors.accountName}
                        errorText={touched.accountName ? errors.accountName : ""}
                    />
                </Grid>
                <Grid item size={{ xs: 12, md: 6 }}>
                    <DynamicFieldAdvanced
                        type="text"
                        label="Account Number"
                        name="accountNumber"
                        value={bankDetails.accountNumber}
                        onChange={handleFieldChange}
                        required
                        error={touched.accountNumber && !!errors.accountNumber}
                        errorText={touched.accountNumber ? errors.accountNumber : ""}
                        numericOnly
                        inputProps={{ minLength: 10, maxLength: 18 }}
                    />
                </Grid>
                <Grid item size={{ xs: 12, md: 6 }}>
                    <DynamicFieldAdvanced
                        type="text"
                        label="Bank Name"
                        name="bankName"
                        value={bankDetails.bankName}
                        onChange={handleFieldChange}
                        required
                        error={touched.bankName && !!errors.bankName}
                        errorText={touched.bankName ? errors.bankName : ""}
                    />
                </Grid>
                <Grid item size={{ xs: 12, md: 6 }}>
                    <DynamicFieldAdvanced
                        type="text"
                        label="IFSC Code"
                        name="ifscCode"
                        value={bankDetails.ifscCode}
                        onChange={handleFieldChange}
                        required
                        error={touched.ifscCode && !!errors.ifscCode}
                        errorText={touched.ifscCode ? errors.ifscCode : ""}
                    />
                </Grid>
                <Grid item size={{ xs: 12, md: 6 }}>
                    <DynamicFieldAdvanced
                        type="text"
                        label="Branch Name"
                        name="branchName"
                        value={bankDetails.branchName}
                        onChange={handleFieldChange}
                    />
                </Grid>
                <Grid item size={{ xs: 12, md: 6 }}>
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileInputChange}
                        accept=".pdf,.jpg,.jpeg,.png"
                        style={{ display: 'none' }}
                    />
                    <DynamicFieldAdvanced
                        type="file"
                        label="Upload Cancellation Check / Bank Passbook"
                        name="passbookDocument"
                        value=""
                        onChange={(name, files) => setBankFile(files[0])}
                        files={bankFile ? [bankFile] : []}
                        onFileRemove={() => setBankFile(null)}
                        onFileChoose={handleFileChoose}
                        accept=".pdf,.jpg,.jpeg,.png"
                        maxSize="4MB"
                        supportedFormats="pdf, jpg, jpeg, png"
                        sx={{ maxWidth: "fit-content !important" }}
                    />
                </Grid>
            </Grid>

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                <Button
                    variant="outlined"
                    onClick={onBack}
                    size="large"
                >
                    Back
                </Button>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        variant="outlined"
                        onClick={handleSkip}
                        size="large"
                        disabled={isSubmitting}
                    >
                        Skip for now
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        size="large"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? "Saving..." : (employeeId ? "Update" : "Save & Next")}
                    </Button>
                </Box>
            </Box>
            <Notification />
        </Box>
    );
};

export default BankDetails;
